import React, { useEffect, useState, useMemo } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { 
    Thermometer, 
    Scale, 
    Layers, 
    Ban, 
    Sparkles, 
    Briefcase, 
    Sprout, 
    Palette, 
    Shirt, 
    Ruler, 
    Trees, 
    Wind,
    Zap,
    Search,
    Cpu,
    LucideIcon
} from 'lucide-react'

// Clothing item types for visual variety
type ClothingType = 'top' | 'bottom' | 'outerwear' | 'shoes' | 'accessory'

interface ClothingTile {
    id: number
    type: ClothingType
    color: string
}

// Color palette matching the app's warm editorial style (no blues!)
const clothingColors = [
    '#E6E5E2', // Paper tertiary
    '#D4D3CF', // Warmer grey
    '#C4C3BF', // Medium neutral
    '#CBBFAE', // Muted sand (accent)
    '#B8AC9A', // Darker sand
    '#A39E8F', // Warm grey
    '#8B8680', // Medium warm
    '#6E6B63', // Dark warm
]

// Generate random clothing tiles
const generateTiles = (count: number): ClothingTile[] => {
    const types: ClothingType[] = ['top', 'bottom', 'outerwear', 'shoes', 'accessory']
    return Array.from({ length: count }, (_, i) => ({
        id: i,
        type: types[i % types.length],
        color: clothingColors[Math.floor(Math.random() * clothingColors.length)],
    }))
}

// Clothing silhouette SVG components
function ClothingSilhouette({ type, color }: { type: ClothingType; color: string }) {
    const size = type === 'accessory' || type === 'shoes' ? 40 : 56

    const silhouettes: Record<ClothingType, React.ReactElement> = {
        top: (
            <svg viewBox="0 0 56 56" fill={color} className="w-full h-full">
                <path d="M14 12 L28 8 L42 12 L48 24 L42 26 L40 18 L40 48 L16 48 L16 18 L14 26 L8 24 Z" />
            </svg>
        ),
        bottom: (
            <svg viewBox="0 0 56 56" fill={color} className="w-full h-full">
                <path d="M16 8 L40 8 L40 16 L42 52 L30 52 L28 24 L26 52 L14 52 L16 16 Z" />
            </svg>
        ),
        outerwear: (
            <svg viewBox="0 0 56 56" fill={color} className="w-full h-full">
                <path d="M12 10 L28 6 L44 10 L50 26 L44 28 L42 18 L44 52 L12 52 L14 18 L12 28 L6 26 Z" />
            </svg>
        ),
        shoes: (
            <svg viewBox="0 0 40 40" fill={color} className="w-full h-full">
                <path d="M6 20 L10 14 L30 14 L36 20 L36 28 L6 28 Z" />
            </svg>
        ),
        accessory: (
            <svg viewBox="0 0 40 40" fill={color} className="w-full h-full">
                <circle cx="20" cy="20" r="12" />
            </svg>
        ),
    }

    return (
        <div style={{ width: size, height: size }} className="opacity-90">
            {silhouettes[type]}
        </div>
    )
}

// Individual Clothing Tile
function Tile({ tile, style }: { tile: ClothingTile; style?: React.CSSProperties }) {
    return (
        <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white/60 backdrop-blur-sm border border-white/40 shadow-md flex items-center justify-center p-3 transform-gpu"
            style={style}
        >
            <div className="relative group">
                <ClothingSilhouette type={tile.type} color={tile.color} />
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-cta/10 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </div>
    )
}

// Typing prompt component
function TypingPrompt({ text }: { text: string }) {
    const [charCount, setCharCount] = useState(0)

    useEffect(() => {
        setCharCount(0)
        const interval = setInterval(() => {
            setCharCount(prev => {
                if (prev < text.length) return prev + 1
                clearInterval(interval)
                return prev
            })
        }, 50)
        return () => clearInterval(interval)
    }, [text])

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            className="flex items-center gap-3 px-6 py-4 bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl rounded-2xl max-w-sm sm:max-w-md mx-auto"
        >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cta/10 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-cta animate-pulse" />
            </div>
            <p className="text-ink font-medium leading-tight">
                {text.slice(0, charCount)}
                <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="inline-block w-[2px] h-[1em] bg-cta ml-1 align-middle"
                />
            </p>
        </motion.div>
    )
}

// Outfit Card Component (for reveal phase)
function OutfitCard({ items, index }: { items: ClothingTile[]; index: number }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                delay: index * 0.2,
                duration: 0.6,
                type: 'spring',
                stiffness: 100
            }}
            className="group relative bg-white/70 backdrop-blur-md rounded-3xl border border-white/40 shadow-card p-4 overflow-hidden"
        >
            {/* Shimmer effect */}
            <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '200%' }}
                transition={{
                    delay: index * 0.2 + 0.8,
                    duration: 1.5,
                    ease: 'easeInOut',
                    repeat: Infinity,
                    repeatDelay: 3
                }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 pointer-events-none"
            />

            {/* Outfit label */}
            <div className="mb-4 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider font-bold text-cta">Option {index + 1}</span>
                <div className="h-1 w-8 bg-paper-tertiary rounded-full" />
            </div>

            {/* 2x2 grid of items */}
            <div className="grid grid-cols-2 gap-3">
                {items.slice(0, 4).map((item, i) => (
                    <motion.div
                        key={item.id}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.2 + 0.3 + (i * 0.1) }}
                        className="aspect-square rounded-xl bg-white/50 border border-paper-tertiary flex items-center justify-center p-2 group-hover:scale-105 transition-transform"
                    >
                        <ClothingSilhouette type={item.type} color={item.color} />
                    </motion.div>
                ))}
            </div>
        </motion.div>
    )
}

// Static fallback for reduced motion
function StaticOutfitCards({ tiles }: { tiles: ClothingTile[] }) {
    const outfits = [
        tiles.slice(0, 4),
        tiles.slice(4, 8),
        tiles.slice(8, 12),
    ]

    return (
        <div className="flex gap-4 items-center justify-center">
            {outfits.map((items, index) => (
                <div
                    key={index}
                    className="bg-paper-alt rounded-2xl border border-paper-tertiary shadow-card p-4"
                >
                    <div className="mb-3">
                        <span className="text-xs font-medium text-ink-tertiary">Outfit {index + 1}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className="w-12 h-12 rounded-lg bg-paper flex items-center justify-center p-1"
                            >
                                <ClothingSilhouette type={item.type} color={item.color} />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    )
}

// Animation phases
type Phase = 'prompt' | 'chaos' | 'organize' | 'analysis' | 'reveal' | 'reset'

// Phase durations in ms
const PHASE_DURATIONS = {
    prompt: 3000,
    chaos: 2500,
    organize: 2000,
    analysis: 4000,
    reveal: 5000,
    reset: 1000,
}

// Typing prompt scenarios
const PROMPT_SCENARIOS = [
    "What should I wear to a dinner in Soho? It's 18°C.",
    "Business casual meeting but make it fashion.",
    "Weekend getaway to the countryside. Cozy but chic.",
]

// Detailed thought process data with Lucide icons
const SCENARIO_THOUGHTS = [
    [
        { text: "18°C Soho Dinner", icon: Thermometer },
        { text: "Smart Casual Balance", icon: Scale },
        { text: "Silk x Wool Textures", icon: Layers },
        { text: "Avoid Heavy Denim", icon: Ban },
        { text: "Stylist: Elevate with Gold", icon: Sparkles },
    ],
    [
        { text: "Business Fashion", icon: Briefcase },
        { text: "Linen/Cotton Mix", icon: Sprout },
        { text: "Neutral Palette", icon: Palette },
        { text: "Texture: Crisp Poplin", icon: Shirt },
        { text: "Avoid Oversized Fit", icon: Ruler },
    ],
    [
        { text: "Country Getaway", icon: Trees },
        { text: "Cashmere Layers", icon: Wind },
        { text: "Leather Accents", icon: Zap },
        { text: "Cozy but Chic", icon: Search },
        { text: "Earth Tones Only", icon: Cpu },
    ]
]

// Intelligence/Brain Animation Component
function IntelligenceBrain({ active }: { active: boolean }) {
    return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.2, filter: 'blur(20px)' }}
                        className="relative w-64 h-64"
                    >
                        {/* The "Brain" Core */}
                        <motion.div
                            animate={{
                                scale: [1, 1.05, 1],
                                opacity: [0.3, 0.5, 0.3],
                            }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute inset-0 bg-cta/10 blur-3xl rounded-full"
                        />
                        
                        {/* Messy connections/intelligence lines */}
                        <svg viewBox="0 0 200 200" className="w-full h-full">
                            {[...Array(12)].map((_, i) => (
                                <motion.path
                                    key={i}
                                    d={`M 100 100 Q ${100 + Math.cos(i) * 80} ${100 + Math.sin(i) * 80} ${100 + Math.cos(i + 0.5) * 100} ${100 + Math.sin(i + 0.5) * 100}`}
                                    stroke="currentColor"
                                    strokeWidth="0.5"
                                    fill="none"
                                    className="text-cta/30"
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{ delay: i * 0.1, duration: 2, repeat: Infinity, repeatType: "reverse" }}
                                />
                            ))}
                        </svg>

                        {/* Pulses */}
                        <motion.div
                            animate={{
                                scale: [1, 2],
                                opacity: [0.5, 0],
                            }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border border-cta/30"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

function ThoughtNode({ thought, index, total }: { thought: { text: string, icon: LucideIcon }, index: number, total: number }) {
    const angle = (index / total) * Math.PI * 2
    const radius = 130
    const x = Math.cos(angle) * radius
    const y = Math.sin(angle) * radius
    const Icon = thought.icon

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x, y }}
            exit={{ opacity: 0, scale: 0, x: x * 1.2, y: y * 1.2 }}
            transition={{ 
                type: 'spring', 
                stiffness: 100, 
                damping: 15,
                delay: 0.5 + index * 0.2 
            }}
            className="absolute px-4 py-2 bg-white/90 backdrop-blur-md border border-paper-tertiary rounded-2xl shadow-xl flex items-center gap-2 z-20"
        >
            <Icon size={14} className="text-cta" />
            <span className="text-[11px] font-bold text-ink whitespace-nowrap uppercase tracking-tight">{thought.text}</span>
        </motion.div>
    )
}

// Scanning/DNA effect that connects tiles
function ConnectionLines({ tiles, active, isMobile, tileCount }: { tiles: ClothingTile[], active: boolean, isMobile: boolean, tileCount: number }) {
    if (!active) return null

    // Use viewBox for proper coordinate system - we'll use a coordinate system where center is at 0,0
    // and convert pixel offsets to this coordinate system
    const viewBoxSize = 1000
    const centerX = viewBoxSize / 2
    const centerY = viewBoxSize / 2

    return (
        <svg 
            className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
            viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
            preserveAspectRatio="xMidYMid meet"
        >
            <AnimatePresence>
                {tiles.map((_tile, i) => {
                    if (i === 0) return null
                    const column = i % 3
                    const row = Math.floor(i / 3)
                    // Convert pixel offsets to viewBox coordinates
                    const pixelOffsetX = (column - 1) * (isMobile ? 90 : 120)
                    const pixelOffsetY = (row - Math.floor(tileCount / 6)) * (isMobile ? 80 : 100)
                    
                    const prevColumn = (i - 1) % 3
                    const prevRow = Math.floor((i - 1) / 3)
                    const prevPixelOffsetX = (prevColumn - 1) * (isMobile ? 90 : 120)
                    const prevPixelOffsetY = (prevRow - Math.floor(tileCount / 6)) * (isMobile ? 80 : 100)

                    // Approximate conversion: assuming a typical container size, convert pixels to viewBox units
                    // For a 1000x1000 viewBox and typical 400px container, 1px ≈ 2.5 viewBox units
                    // Use a conservative estimate that works across screen sizes
                    const pxToViewBox = viewBoxSize / 400 // approximate container size
                    const x = centerX + pixelOffsetX * pxToViewBox
                    const y = centerY + pixelOffsetY * pxToViewBox
                    const prevX = centerX + prevPixelOffsetX * pxToViewBox
                    const prevY = centerY + prevPixelOffsetY * pxToViewBox

                    return (
                        <motion.line
                            key={`line-${i}`}
                            x1={prevX}
                            y1={prevY}
                            x2={x}
                            y2={y}
                            stroke="currentColor"
                            strokeWidth="1"
                            strokeDasharray="4 4"
                            className="text-cta/20"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                        />
                    )
                })}
            </AnimatePresence>
        </svg>
    )
}

export function HeroAnimation() {
    const prefersReducedMotion = useReducedMotion()
    const [phase, setPhase] = useState<Phase>('prompt')
    const [scenarioIndex, setScenarioIndex] = useState(0)
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

    // Use different tile counts based on screen size (detected via media query)
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 640px)')
        setIsMobile(mediaQuery.matches)

        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
        mediaQuery.addEventListener('change', handler)
        return () => mediaQuery.removeEventListener('change', handler)
    }, [])

    const tileCount = isMobile ? 8 : 14
    const tiles = useMemo(() => generateTiles(tileCount), [tileCount])

    // Generate random chaos positions
    const chaosPositions = useMemo(() => {
        return tiles.map(() => ({
            x: (Math.random() - 0.5) * 280,
            y: (Math.random() - 0.5) * 350,
            rotation: (Math.random() - 0.5) * 30,
            delay: Math.random() * 0.5,
        }))
    }, [tiles])

    // Animation loop
    useEffect(() => {
        if (prefersReducedMotion) return

        const phases: Phase[] = ['prompt', 'chaos', 'organize', 'analysis', 'reveal', 'reset']
        let currentIndex = phases.indexOf(phase)

        const nextPhase = () => {
            const nextIndex = (currentIndex + 1) % phases.length
            if (nextIndex === 0) {
                setScenarioIndex((prev) => (prev + 1) % PROMPT_SCENARIOS.length)
            }
            setPhase(phases[nextIndex])
        }

        const timer = setTimeout(nextPhase, PHASE_DURATIONS[phase])
        return () => clearTimeout(timer)
    }, [phase, prefersReducedMotion])

    // Track mouse for parallax
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePosition({
                x: (e.clientX / window.innerWidth - 0.5) * 40,
                y: (e.clientY / window.innerHeight - 0.5) * 40,
            })
        }
        window.addEventListener('mousemove', handleMouseMove)
        return () => window.removeEventListener('mousemove', handleMouseMove)
    }, [])

    // Reduced motion fallback
    if (prefersReducedMotion) {
        return (
            <div className="relative w-full h-full flex items-center justify-center">
                <StaticOutfitCards tiles={tiles} />
            </div>
        )
    }

    return (
        <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
            <AnimatePresence mode="popLayout">
                {/* Phase 0: Prompt Typing */}
                {phase === 'prompt' && (
                    <motion.div
                        key="prompt"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.5 }}
                        className="z-20"
                    >
                        <TypingPrompt text={PROMPT_SCENARIOS[scenarioIndex]} />
                    </motion.div>
                )}

                {/* Phase 1: Chaos - tiles scattered with parallax */}
                {phase === 'chaos' && (
                    <motion.div
                        key="chaos"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 1 }} // Don't fade out yet, transition tiles to grid
                        transition={{ duration: 0.5 }}
                        className="absolute inset-0 flex items-center justify-center"
                    >
                        {tiles.map((tile, i) => (
                            <motion.div
                                key={tile.id}
                                initial={{
                                    x: chaosPositions[i].x * 1.5,
                                    y: chaosPositions[i].y * 1.5,
                                    opacity: 0,
                                    scale: 0.5,
                                }}
                                animate={{
                                    x: chaosPositions[i].x + (mousePosition.x * (i % 3 + 1) * 0.2),
                                    y: chaosPositions[i].y + (mousePosition.y * (i % 3 + 1) * 0.2),
                                    rotate: chaosPositions[i].rotation,
                                    opacity: 1,
                                    scale: 1,
                                }}
                                transition={{
                                    delay: chaosPositions[i].delay,
                                    duration: 1.2,
                                    type: 'spring',
                                    damping: 20,
                                }}
                                className="absolute"
                            >
                                <Tile tile={tile} />
                            </motion.div>
                        ))}
                    </motion.div>
                )}

                {/* Phase 2: Organize - tiles form grid with "AI Magic" */}
                {phase === 'organize' && (
                    <motion.div
                        key="organize"
                        initial={{ opacity: 1 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, filter: 'blur(10px)' }}
                        className="absolute inset-0 flex items-center justify-center"
                    >
                        <ConnectionLines tiles={tiles} active={true} isMobile={isMobile} tileCount={tileCount} />
                        {tiles.map((tile, i) => {
                            const column = i % 3
                            const row = Math.floor(i / 3)
                            const xOffset = (column - 1) * (isMobile ? 90 : 120)
                            const yOffset = (row - Math.floor(tileCount / 6)) * (isMobile ? 80 : 100)

                            return (
                                <motion.div
                                    key={tile.id}
                                    initial={{
                                        x: chaosPositions[i].x,
                                        y: chaosPositions[i].y,
                                        rotate: chaosPositions[i].rotation,
                                        scale: 1,
                                    }}
                                    animate={{
                                        x: xOffset,
                                        y: yOffset,
                                        rotate: 0,
                                        scale: 0.9,
                                    }}
                                    transition={{
                                        duration: 0.8,
                                        ease: [0.34, 1.56, 0.64, 1], // Custom bouncy ease
                                        delay: i * 0.03,
                                    }}
                                    className="absolute"
                                >
                                    <Tile tile={tile} />
                                    {/* Trail effect */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5] }}
                                        transition={{ duration: 0.8, delay: i * 0.03 }}
                                        className="absolute inset-0 bg-cta/20 blur-xl rounded-full"
                                    />
                                </motion.div>
                            )
                        })}
                    </motion.div>
                )}

                {/* Phase 2.5: Analysis - Brain & Thoughts */}
                {phase === 'analysis' && (
                    <motion.div
                        key="analysis"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                    >
                        <IntelligenceBrain active={true} />
                        <div className="relative w-full h-full flex items-center justify-center">
                            {SCENARIO_THOUGHTS[scenarioIndex].map((thought, i) => (
                                <ThoughtNode 
                                    key={i} 
                                    thought={thought} 
                                    index={i} 
                                    total={SCENARIO_THOUGHTS[scenarioIndex].length} 
                                />
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Phase 3 & 4: Reveal & Reset - unified to prevent re-mounting */}
                {(phase === 'reveal' || phase === 'reset') && (
                    <motion.div
                        key="results-container"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ 
                            opacity: phase === 'reveal' ? 1 : 0, 
                            scale: phase === 'reveal' ? 1 : 1.1,
                            filter: phase === 'reveal' ? 'blur(0px)' : 'blur(10px)'
                        }}
                        transition={{ 
                            duration: phase === 'reveal' ? 0.6 : 0.8,
                            ease: phase === 'reveal' ? 'easeOut' : 'easeIn'
                        }}
                        className="absolute inset-0 flex items-center justify-center gap-3 sm:gap-6 p-4 w-full"
                    >
                        <div className="flex items-center justify-center gap-3 sm:gap-6 w-full max-w-5xl mx-auto">
                            <OutfitCard items={tiles.slice(0, 4)} index={0} />
                            <OutfitCard items={tiles.slice(4, 8)} index={1} />
                            {!isMobile && <OutfitCard items={tiles.slice(8, 12)} index={2} />}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
